import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { ClipboardList, Plus, Edit, Eye, MoreVertical, Thermometer, Camera, CheckSquare, Video, Mic, FileText, Upload, Copy, Trash2, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutWrapper } from '@/components/LayoutWrapper';
import { FileUploadDialog } from '@/components/dialogs/FileUploadDialog';
import { DocumentImportDialog } from '@/components/dialogs/DocumentImportDialog';
import { TemplateItemConfigurator } from '@/components/TemplateItemConfigurator';
import { sampleTemplates } from '@/data/sampleTemplates';

interface Template {
  id: string;
  name: string;
  description: string | null;
  frequency: string | null;
  is_active: boolean;
  items: any[];
  created_at: string;
}

interface ImportedTemplate {
  id: string;
  sender_email: string;
  original_filename: string;
  document_url: string;
  status: string;
  created_at: string;
  processed_at: string | null;
  generated_template: any;
  error_message: string | null;
  import_source: string;
}

const Templates = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [importedTemplates, setImportedTemplates] = useState<ImportedTemplate[]>([]);
  const [documentImports, setDocumentImports] = useState<ImportedTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [documentImportDialogOpen, setDocumentImportDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [deletingImport, setDeletingImport] = useState<string | null>(null);
  const [configuringTemplate, setConfiguringTemplate] = useState<ImportedTemplate | null>(null);
  const [configuredItems, setConfiguredItems] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'daily'
  });


  // Egon sauce temperature monitoring template
  const egonSauceItems = [
    // Hot holding sauces
    {
      id: 'hot_pepper_sauce',
      type: 'temperature',
      label: 'Pepper Sauce (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['Pepper Sauce', 'Chipotle Sauce', 'Jalapeño Sauce', 'Habanero Sauce']
    },
    {
      id: 'hot_birria_sauce',
      type: 'temperature',
      label: 'Birria Sauce (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['Birria Sauce', 'Consommé', 'Beef Broth']
    },
    {
      id: 'hot_taco_sauce',
      type: 'temperature',
      label: 'Taco Sauce (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['Taco Sauce', 'Salsa Roja', 'Salsa Verde', 'Hot Sauce']
    },
    {
      id: 'hot_bolognese',
      type: 'temperature',
      label: 'Bolognese (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['Bolognese', 'Marinara', 'Meat Sauce', 'Arrabbiata']
    },
    {
      id: 'hot_fish_soup',
      type: 'temperature',
      label: 'Fish Soup (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['Fish Soup', 'Seafood Broth', 'Bouillabaisse', 'Fish Stock']
    },
    {
      id: 'hot_bbq_sauce',
      type: 'temperature',
      label: 'BBQ Sauce (Hot Holding)',
      description: 'Target: ≥ 63°C (≥ 145°F)',
      required: true,
      min_value: 63,
      unit: '°C',
      sauce_options: ['BBQ Sauce', 'Smoky BBQ', 'Kansas City BBQ', 'Carolina BBQ']
    },
    {
      id: 'hot_bearnaise_sauce',
      type: 'temperature',
      label: 'Béarnaise Sauce (Hot Holding)',
      description: 'Target: 55–60°C (131–140°F)',
      required: true,
      min_value: 55,
      max_value: 60,
      unit: '°C',
      sauce_options: ['Béarnaise Sauce', 'Hollandaise', 'Butter Sauce']
    },
    // Cold holding sauces
    {
      id: 'cold_mango_salsa',
      type: 'temperature',
      label: 'Mango Salsa (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Mango Salsa', 'Tropical Salsa', 'Fruit Salsa']
    },
    {
      id: 'cold_mango_sauce',
      type: 'temperature',
      label: 'Mango Sauce (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Mango Sauce', 'Mango Chutney', 'Tropical Sauce']
    },
    {
      id: 'cold_chipotle_aioli',
      type: 'temperature',
      label: 'Chipotle Aioli (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Chipotle Aioli', 'Garlic Aioli', 'Spicy Mayo']
    },
    {
      id: 'cold_hamburger_dressing',
      type: 'temperature',
      label: 'Hamburger Dressing (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Hamburger Dressing', 'Burger Sauce', 'Special Sauce']
    },
    {
      id: 'cold_salsa',
      type: 'temperature',
      label: 'Salsa (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Salsa', 'Pico de Gallo', 'Salsa Verde', 'Salsa Roja']
    },
    {
      id: 'cold_guacamole',
      type: 'temperature',
      label: 'Guacamole (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Guacamole', 'Avocado Dip', 'Chunky Guacamole']
    },
    {
      id: 'cold_remoulade',
      type: 'temperature',
      label: 'Remoulade (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Remoulade', 'Tartar Sauce', 'Cajun Remoulade']
    },
    {
      id: 'cold_pea_puree',
      type: 'temperature',
      label: 'Pea Purée (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Pea Purée', 'Green Pea Sauce', 'Mint Pea Sauce']
    },
    {
      id: 'cold_carbonara_sauce',
      type: 'temperature',
      label: 'Carbonara Sauce (Cold-held)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Carbonara Sauce', 'Cream Sauce', 'Alfredo']
    },
    {
      id: 'cold_curry_sauce',
      type: 'temperature',
      label: 'Curry Sauce (Cold Prep)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Curry Sauce', 'Thai Curry', 'Indian Curry', 'Coconut Curry']
    },
    {
      id: 'cold_sweet_sour_sauce',
      type: 'temperature',
      label: 'Sweet & Sour Sauce (Cold Holding)',
      description: 'Target: 1-4°C (34-39°F)',
      required: true,
      min_value: 1,
      max_value: 4,
      unit: '°C',
      sauce_options: ['Sweet & Sour Sauce', 'Asian Sweet Sauce', 'Pineapple Sauce']
    },
    // Additional monitoring items
    {
      id: 'photo_sauce_storage',
      type: 'photo',
      label: 'Sauce Storage Area',
      description: 'Document proper storage conditions',
      required: true
    },
    {
      id: 'voice_observations',
      type: 'voice_note',
      label: 'Temperature Observations',
      description: 'Record any temperature concerns or notes',
      required: false
    }
  ];

  useEffect(() => {
    fetchTemplates();
    fetchImportedTemplates();
    fetchDocumentImports();
  }, [user]);

  const fetchTemplates = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('checklist_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates((data || []).map(template => ({
        ...template,
        items: Array.isArray(template.items) ? template.items : []
      })));
    } catch (error: any) {
      toast({
        title: "Error loading templates",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchImportedTemplates = async () => {
    if (!user) return;

    try {
      // Only fetch email imports for the email dialog
      const { data, error } = await supabase
        .from('imported_templates')
        .select('*')
        .eq('import_source', 'email')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setImportedTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching imported templates:', error);
    }
  };

  const fetchDocumentImports = async () => {
    if (!user) return;

    try {
      // Fetch document imports for the main page
      const { data, error } = await supabase
        .from('imported_templates')
        .select('*')
        .eq('import_source', 'document_import')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setDocumentImports(data || []);
    } catch (error: any) {
      console.error('Error fetching document imports:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFrequencyChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      frequency: value
    }));
  };

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setCreating(true);
    try {
      // Get user's tenant
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userRole) throw new Error('User not associated with any organization');

      const { error } = await supabase
        .from('checklist_templates')
        .insert({
          name: formData.name,
          description: formData.description || null,
          frequency: formData.frequency,
          tenant_id: userRole.tenant_id,
          items: [],
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Template created!",
        description: "Your new checklist template has been created. You can now add items to it.",
      });

      setDialogOpen(false);
      setFormData({ name: '', description: '', frequency: 'daily' });
      fetchTemplates();
    } catch (error: any) {
      toast({
        title: "Error creating template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const createTemplateFromPreset = async (templateType: 'egon' | 'comprehensive' | 'basicCleaning') => {
    if (!user) return;

    setCreating(true);
    try {
      // Get user's tenant
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userRole) throw new Error('User not associated with any organization');

      let templateData;
      switch (templateType) {
        case 'egon':
          templateData = {
            name: 'Egon - Sauce Temperature Monitoring',
            description: 'Comprehensive temperature monitoring for hot and cold holding sauces with specific target ranges for optimal food safety compliance.',
            frequency: 'daily',
            items: egonSauceItems
          };
          break;
        case 'comprehensive':
          templateData = {
            name: sampleTemplates.comprehensive.name,
            description: sampleTemplates.comprehensive.description,
            frequency: 'daily',
            items: sampleTemplates.comprehensive.items
          };
          break;
        case 'basicCleaning':
          templateData = {
            name: sampleTemplates.basicCleaning.name,
            description: sampleTemplates.basicCleaning.description,
            frequency: 'daily',
            items: sampleTemplates.basicCleaning.items
          };
          break;
      }

      const { error } = await supabase
        .from('checklist_templates')
        .insert({
          ...templateData,
          tenant_id: userRole.tenant_id,
          is_active: true
        });

      if (error) throw error;

      toast({
        title: "Template Created!",
        description: `"${templateData.name}" has been added to your templates.`,
      });

      setTemplateSelectorOpen(false);
      fetchTemplates();
    } catch (error: any) {
      toast({
        title: "Error creating template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const approveImportedTemplate = async (importedTemplate: ImportedTemplate, autoRun = false) => {
    // Open configuration dialog first
    setConfiguringTemplate(importedTemplate);
    const template = importedTemplate.generated_template;
    // Set default media settings for all items
    const itemsWithDefaults = (template.items || []).map((item: any) => ({
      ...item,
      supports_photos: item.supports_photos ?? true,
      requires_photos: item.requires_photos ?? false,
      supports_comments: item.supports_comments ?? true,
      requires_comments: item.requires_comments ?? false,
      min_photos: item.min_photos ?? 0,
      max_photos: item.max_photos ?? 5,
    }));
    setConfiguredItems(itemsWithDefaults);
  };

  const finalizeApproval = async (autoRun = false) => {
    if (!user || !configuringTemplate) return;

    setCreating(true);
    try {
      // Get user's tenant
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userRole) throw new Error('User not associated with any organization');

      const template = configuringTemplate.generated_template;
      
      // Create template with configured items
      const templateSource = configuringTemplate.import_source === 'document_import' ? 'document_import' : 'email_import';
      const { data: newTemplate, error } = await supabase
        .from('checklist_templates')
        .insert({
          name: template.name,
          description: template.description || null,
          frequency: template.frequency || 'daily',
          tenant_id: userRole.tenant_id,
          items: configuredItems, // Use configured items
          is_active: true,
          imported_template_id: configuringTemplate.id,
          template_source: templateSource,
          original_document_url: configuringTemplate.document_url
        })
        .select()
        .single();

      if (error) throw error;

      // Update imported template status
      await supabase
        .from('imported_templates')
        .update({ 
          status: 'approved',
          approved_by: user.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', configuringTemplate.id);

      toast({
        title: "Template approved!",
        description: `"${template.name}" has been ${autoRun ? 'approved and ready to run' : 'added to your templates'}.`,
      });

      fetchTemplates();
      fetchImportedTemplates();
      fetchDocumentImports();
      setConfiguringTemplate(null);

      // Auto-navigate to checklist creation if autoRun is true
      if (autoRun && newTemplate) {
        navigate(`/checklists/new?template=${newTemplate.id}`);
      }
    } catch (error: any) {
      toast({
        title: "Error approving template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const deleteImportedTemplate = async (importId: string, fileName: string) => {
    if (!user) return;

    if (!confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingImport(importId);
    try {
      const { error } = await supabase
        .from('imported_templates')
        .delete()
        .eq('id', importId);

      if (error) throw error;

      toast({
        title: "Import deleted!",
        description: `"${fileName}" has been deleted successfully.`,
      });

      fetchImportedTemplates();
      fetchDocumentImports();
    } catch (error: any) {
      toast({
        title: "Error deleting import",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingImport(null);
    }
  };

  const copyTemplate = async (templateId: string, templateName: string) => {
    if (!user) return;

    setCopying(templateId);
    try {
      // Get the original template
      const { data: originalTemplate, error: fetchError } = await supabase
        .from('checklist_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError) throw fetchError;

      // Get user's tenant
      const { data: userRole } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!userRole) throw new Error('User not associated with any organization');

      // Create a copy
      const { error } = await supabase
        .from('checklist_templates')
        .insert({
          name: `${originalTemplate.name} (Copy)`,
          description: originalTemplate.description,
          frequency: originalTemplate.frequency,
          tenant_id: userRole.tenant_id,
          items: originalTemplate.items,
          is_active: true,
          copied_from_id: templateId,
          template_category: originalTemplate.template_category || 'general'
        });

      if (error) throw error;

      toast({
        title: "Template copied!",
        description: `"${templateName}" has been duplicated successfully.`,
      });

      fetchTemplates();
    } catch (error: any) {
      toast({
        title: "Error copying template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCopying(null);
    }
  };

  const deleteTemplate = async (templateId: string, templateName: string) => {
    if (!user) return;

    if (!confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(templateId);
    try {
      const { error } = await supabase
        .from('checklist_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;

      toast({
        title: "Template deleted!",
        description: `"${templateName}" has been deleted successfully.`,
      });

      fetchTemplates();
    } catch (error: any) {
      toast({
        title: "Error deleting template",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'temperature':
        return <Thermometer className="h-4 w-4 text-blue-500" />;
      case 'photo':
        return <Camera className="h-4 w-4 text-green-500" />;
      case 'video':
        return <Video className="h-4 w-4 text-purple-500" />;
      case 'voice_note':
        return <Mic className="h-4 w-4 text-orange-500" />;
      case 'checklist':
        return <CheckSquare className="h-4 w-4 text-blue-500" />;
      case 'visual':
        return <Eye className="h-4 w-4 text-purple-500" />;
      case 'checkbox':
        return <CheckSquare className="h-4 w-4 text-orange-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      {/* Header */}
      <header className="glass-nav">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-crystal-white">
              ← Back to Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-crystal-white">Checklist Templates</h1>
            <p className="text-sm text-muted-foreground">Create and manage your HACCP checklist templates</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="cta-secondary"
              onClick={() => setDocumentImportDialogOpen(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Documents
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="cta-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="glass-panel">
                <DialogHeader>
                  <DialogTitle>Create New Template</DialogTitle>
                  <DialogDescription>
                    Create a new checklist template with predefined food safety checks.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={createTemplate}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Template Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Daily Opening Checklist"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="glass-input"
                        required
                        minLength={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        name="description"
                        placeholder="Describe what this checklist covers..."
                        value={formData.description}
                        onChange={handleInputChange}
                        className="glass-input"
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="frequency">Frequency</Label>
                      <Select value={formData.frequency} onValueChange={handleFrequencyChange}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="as_needed">As Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setDialogOpen(false)}
                      className="cta-secondary"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="cta-primary"
                      disabled={creating || !formData.name.trim()}
                    >
                      {creating ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Creating...
                        </div>
                      ) : (
                        'Create Template'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={templateSelectorOpen} onOpenChange={setTemplateSelectorOpen}>
              <DialogTrigger asChild>
                <Button 
                  disabled={creating}
                  variant="outline"
                  className="cta-secondary"
                >
                  <Thermometer className="h-4 w-4 mr-2" />
                  Create from Template
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Choose a Template</DialogTitle>
                  <DialogDescription>
                    Select a pre-configured template to get started quickly
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('egon')}>
                    <div className="flex items-start gap-3">
                      <Thermometer className="h-6 w-6 text-primary mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold mb-2">Egon - Sauce Temperature Monitoring</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Comprehensive temperature monitoring for hot and cold holding sauces with 18 temperature checks.
                        </p>
                        <div className="flex gap-2">
                          <Badge variant="secondary">Daily</Badge>
                          <Badge variant="secondary">18 Items</Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('comprehensive')}>
                    <div className="flex items-start gap-3">
                      <ClipboardList className="h-6 w-6 text-primary mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold mb-2">Kitchen Basics - Comprehensive HACCP</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Complete checklist covering hot/cold holding, cleaning, restaurant and bar checks.
                        </p>
                        <div className="flex gap-2">
                          <Badge variant="secondary">Daily</Badge>
                          <Badge variant="secondary">Multiple Areas</Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('basicCleaning')}>
                    <div className="flex items-start gap-3">
                      <CheckSquare className="h-6 w-6 text-primary mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold mb-2">Daily Cleaning Checklist</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          Essential daily cleaning and sanitization tasks for food safety compliance.
                        </p>
                        <div className="flex gap-2">
                          <Badge variant="secondary">Daily</Badge>
                          <Badge variant="secondary">Cleaning</Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
                {creating && (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                    Creating template...
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Pending Document Imports Section */}
        {documentImports.length > 0 && (
          <div className="mb-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-crystal-white mb-2">Pending Document Imports</h2>
              <p className="text-sm text-muted-foreground">Review and approve templates generated from your uploaded documents</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {documentImports.map((imported) => (
                <Card key={imported.id} className="glass-panel">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Upload className="h-4 w-4 text-primary" />
                          {imported.original_filename}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            variant={
                              imported.status === 'completed' ? 'default' : 
                              imported.status === 'processing' ? 'secondary' : 
                              imported.status === 'failed' ? 'destructive' : 'outline'
                            }
                          >
                            {imported.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(imported.created_at).toLocaleDateString()}
                          </span>
                        </div>
                       </div>
                       <div className="flex items-center gap-2">
                         {imported.status === 'completed' && imported.generated_template && (
                           <>
                             <Button 
                               size="sm" 
                               onClick={() => approveImportedTemplate(imported)}
                               disabled={creating}
                               className="cta-secondary"
                             >
                               Approve & Use
                             </Button>
                             <Button 
                               size="sm" 
                               onClick={() => approveImportedTemplate(imported, true)}
                               disabled={creating}
                               className="cta-primary"
                             >
                               Approve & Run
                             </Button>
                           </>
                         )}
                         <Button 
                           size="sm" 
                           variant="outline"
                           onClick={() => deleteImportedTemplate(imported.id, imported.original_filename)}
                           disabled={deletingImport === imported.id}
                           className="cta-secondary text-red-400 hover:text-red-300"
                         >
                           {deletingImport === imported.id ? (
                             <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400" />
                           ) : (
                             <Trash2 className="h-4 w-4" />
                           )}
                         </Button>
                       </div>
                    </div>
                  </CardHeader>
                  {imported.generated_template && (
                    <CardContent>
                      <div className="space-y-2">
                        <p className="font-medium text-sm text-crystal-white">
                          Generated: "{imported.generated_template.name}"
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {imported.generated_template.items?.length || 0} check items
                        </p>
                        {imported.generated_template.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {imported.generated_template.description}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  )}
                  {imported.status === 'failed' && imported.error_message && (
                    <CardContent>
                      <p className="text-xs text-destructive">
                        Error: {imported.error_message}
                      </p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {templates.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-crystal-white mb-2">No templates yet</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create your first checklist template to standardize food safety procedures across your organization.
            </p>
            <div className="flex gap-2 justify-center">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="cta-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Template
                  </Button>
                </DialogTrigger>
              </Dialog>
              <Dialog open={templateSelectorOpen} onOpenChange={setTemplateSelectorOpen}>
                <DialogTrigger asChild>
                  <Button 
                    disabled={creating}
                    variant="outline"
                    className="cta-secondary"
                  >
                    <Thermometer className="h-4 w-4 mr-2" />
                    Create from Template
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Choose a Template</DialogTitle>
                    <DialogDescription>
                      Select a pre-configured template to get started quickly
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('egon')}>
                      <div className="flex items-start gap-3">
                        <Thermometer className="h-6 w-6 text-primary mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2">Egon - Sauce Temperature Monitoring</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Comprehensive temperature monitoring for hot and cold holding sauces with 18 temperature checks.
                          </p>
                          <div className="flex gap-2">
                            <Badge variant="secondary">Daily</Badge>
                            <Badge variant="secondary">18 Items</Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('comprehensive')}>
                      <div className="flex items-start gap-3">
                        <ClipboardList className="h-6 w-6 text-primary mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2">Kitchen Basics - Comprehensive HACCP</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Complete checklist covering hot/cold holding, cleaning, restaurant and bar checks.
                          </p>
                          <div className="flex gap-2">
                            <Badge variant="secondary">Daily</Badge>
                            <Badge variant="secondary">Multiple Areas</Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4 hover:border-primary cursor-pointer transition-all" onClick={() => createTemplateFromPreset('basicCleaning')}>
                      <div className="flex items-start gap-3">
                        <CheckSquare className="h-6 w-6 text-primary mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2">Daily Cleaning Checklist</h3>
                          <p className="text-sm text-muted-foreground mb-3">
                            Essential daily cleaning and sanitization tasks for food safety compliance.
                          </p>
                          <div className="flex gap-2">
                            <Badge variant="secondary">Daily</Badge>
                            <Badge variant="secondary">Cleaning</Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                  {creating && (
                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                      Creating template...
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="glass-panel hover:glass-panel-elevated transition-all duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-primary" />
                        {template.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant={template.is_active ? "default" : "secondary"} className="text-xs">
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {template.frequency}
                        </Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  
                  <div className="space-y-3">
                    <div className="text-sm">
                      <span className="font-medium text-crystal-white">{template.items.length}</span>
                      <span className="text-muted-foreground"> check items</span>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2">
                      {template.items.slice(0, 4).map((item, index) => (
                        <div key={index} className="flex items-center justify-center p-2 rounded-lg bg-accent/10">
                          {getItemIcon(item.type)}
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center gap-2 pt-2">
                      <Link to={`/templates/${template.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full cta-secondary">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => copyTemplate(template.id, template.name)}
                        disabled={copying === template.id}
                        className="cta-secondary"
                      >
                        {copying === template.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deleteTemplate(template.id, template.name)}
                        disabled={deleting === template.id}
                        className="cta-secondary text-red-400 hover:text-red-300"
                      >
                        {deleting === template.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                      <Link to={`/checklists/new?template=${template.id}`}>
                        <Button size="sm" className="cta-primary">
                          <Plus className="h-4 w-4 mr-2" />
                          Run
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* File Upload Dialog */}
      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUploadComplete={() => {
          fetchTemplates();
          fetchImportedTemplates();
        }}
      />

      <DocumentImportDialog
        open={documentImportDialogOpen}
        onOpenChange={setDocumentImportDialogOpen}
        onImportComplete={() => {
          fetchDocumentImports();
          toast({
            title: "Document uploaded!",
            description: "Your document is being processed. Check the pending imports section below.",
          });
        }}
      />

      {/* Template Item Configurator */}
      {configuringTemplate && (
        <TemplateItemConfigurator
          open={!!configuringTemplate}
          onOpenChange={(open) => !open && setConfiguringTemplate(null)}
          items={configuredItems}
          onSave={(items) => {
            setConfiguredItems(items);
            finalizeApproval(false);
          }}
        />
      )}
    </div>
  );
};

export default Templates;